import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const CategoryCarousel = ({ title, subcategories, genderId, autoPlay = false }) => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [maxPages, setMaxPages] = useState(0);

  // Pagination Logic
  const calculatePagination = () => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth } = scrollRef.current;
      const pages = Math.ceil(scrollWidth / clientWidth);
      setMaxPages(pages > 1 ? pages : 0);
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
      setActiveIndex(index);
    }
  };

  // Auto Scroll Logic
  useEffect(() => {
    let interval;
    if (autoPlay && maxPages > 1) {
      interval = setInterval(() => {
        const nextIndex = (activeIndex + 1) % maxPages;
        scrollToPage(nextIndex);
      }, 3000); // Scrolls every 3 seconds
    }
    return () => clearInterval(interval);
  }, [autoPlay, activeIndex, maxPages]);

  useEffect(() => {
    calculatePagination();
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', calculatePagination);
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculatePagination);
    };
  }, [subcategories]);

  const scrollToPage = (index) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: index * scrollRef.current.clientWidth,
        behavior: 'smooth'
      });
    }
  };

  if (!subcategories || subcategories.length === 0) return null;

  return (
    <div className="relative group">
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth hide-scrollbar snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {subcategories.map((sub) => (
            <Link key={sub.name} to={`/products?gender=${genderId}&category=${sub.value}`}>
              {/* [UPDATED] Increased Card Size */}
              {/* Mobile: min-w-[220px] | Desktop: min-w-[280px] */}
              <div className="min-w-[220px] md:min-w-[280px] snap-start group/card p-3 border border-gray-200 hover:shadow-lg transition-all duration-300 rounded-xl bg-white">
                
                {/* [UPDATED] Increased Image Height */}
                {/* Mobile: h-[320px] | Desktop: h-[400px] */}
                <div className='h-[320px] md:h-[400px] w-full block overflow-hidden rounded-xl bg-gray-100 relative'>
                  <img
                    src={sub.image || "https://placehold.co/400x600?text=Category"}
                    alt={sub.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-110"
                  />
                </div>
                
                <div className="mt-4 text-center">
                  <h3 className="font-bold h-10 text-base md:text-lg line-clamp-2 text-gray-900 uppercase tracking-wide">
                    {sub.name}
                  </h3>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* DYNAMIC DOTS */}
        {maxPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: maxPages }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollToPage(idx)}
                className={`transition-all duration-300 rounded-full 
                  ${activeIndex === idx ? 'w-2.5 h-2.5 bg-gray-800 scale-110' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        )}
    </div>
  );
};